import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Bell, BellOff } from 'lucide-react'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export default function PushNotificationManager() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const [subscribed, setSubscribed] = useState(false)
  const [supported, setSupported] = useState(false)

  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    setSupported(isSupported)
    if (isSupported && Notification.permission === 'granted') {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setSubscribed(!!sub)
        })
      })
    }
  }, [])

  const subscribe = async () => {
    try {
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') return

      const reg = await navigator.serviceWorker.ready
      const { data } = await api.get('/push/vapid-key')
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey)

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })

      const json = subscription.toJSON()
      await api.post('/push/subscribe', {
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh || '',
        auth: json.keys?.auth || '',
      })
      setSubscribed(true)
    } catch (err) {
      console.error('Push subscription failed:', err)
    }
  }

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.ready
      const subscription = await reg.pushManager.getSubscription()
      if (subscription) {
        await api.delete('/push/unsubscribe', { data: { endpoint: subscription.endpoint } })
        await subscription.unsubscribe()
      }
      setSubscribed(false)
    } catch (err) {
      console.error('Push unsubscribe failed:', err)
    }
  }

  if (!supported) return null

  if (permission === 'granted' && subscribed) {
    return (
      <button
        onClick={unsubscribe}
        className="p-2 text-green-600 hover:text-green-700 rounded-lg hover:bg-green-50 transition-colors"
        title="Push notifications enabled"
      >
        <Bell size={20} />
      </button>
    )
  }

  if (permission === 'denied') {
    return (
      <button
        className="p-2 text-slate-300 rounded-lg cursor-not-allowed"
        title="Notifications blocked in browser settings"
        disabled
      >
        <BellOff size={20} />
      </button>
    )
  }

  return (
    <button
      onClick={subscribe}
      className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
      title="Enable push notifications"
    >
      <BellOff size={20} />
    </button>
  )
}
